import {
  AUTH_ERROR_MESSAGES,
  AUTH_REFRESH_BUFFER_MS,
} from "../constants/auth";
import {
  clearAuthState,
  getStoredAuthSession,
  isTvLiveDashboardSession,
  isUserActive,
  saveAuthState,
} from "../utils/authStorage";
import { refreshCognitoSession, signOutCognitoUser } from "./cognitoAuth";

export const AUTH_SESSION_UPDATED_EVENT = "pargro:auth-session-updated";
export const AUTH_SESSION_EXPIRED_EVENT = "pargro:auth-session-expired";

let refreshPromise = null;

function publishSessionEvent(name, detail) {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  }
}

export function expireAuthSession() {
  signOutCognitoUser();
  clearAuthState();
  publishSessionEvent(AUTH_SESSION_EXPIRED_EVENT);
}

export function shouldRefreshSession(session, now = Date.now()) {
  return Boolean(
    session?.expiresAt &&
      Number(session.expiresAt) <= now + AUTH_REFRESH_BUFFER_MS,
  );
}

export async function refreshAuthSession({ force = false } = {}) {
  const session = getStoredAuthSession();

  if (!session) {
    throw new Error(AUTH_ERROR_MESSAGES.sessionExpired);
  }

  if (!force && !shouldRefreshSession(session)) {
    return session;
  }

  const canRefreshInactiveSession = isTvLiveDashboardSession(session);

  if (
    !session.refreshToken ||
    (!isUserActive(session) && !canRefreshInactiveSession)
  ) {
    if (Number(session.expiresAt) <= Date.now() || force) {
      expireAuthSession();
      throw new Error(AUTH_ERROR_MESSAGES.sessionExpired);
    }

    return session;
  }

  if (!refreshPromise) {
    refreshPromise = refreshCognitoSession(session)
      .then((tokenSession) => {
        const nextSession = {
          ...session,
          ...tokenSession,
          lastActivityAt: session.lastActivityAt,
        };

        saveAuthState(nextSession);
        publishSessionEvent(AUTH_SESSION_UPDATED_EVENT, nextSession);
        return nextSession;
      })
      .catch((error) => {
        expireAuthSession();
        throw error;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

export async function ensureValidAccessToken() {
  const session = await refreshAuthSession();

  if (!session?.accessToken || Number(session.expiresAt) <= Date.now()) {
    expireAuthSession();
    throw new Error(AUTH_ERROR_MESSAGES.sessionExpired);
  }

  return session.accessToken;
}
